import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { imgUrl, inr, durationLabel, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { RateChartDialog } from "@/components/RateChart";
import { PaymentDetails } from "@/components/PaymentDetails";
import { MaturityTracker } from "@/components/MaturityTracker";
import { ProfileSection } from "@/components/ProfileSection";
import { KycSection } from "@/components/KycSection";
import { SupportChat } from "@/components/SupportChat";
import { ReferralCard, StatementButton } from "@/components/InvestorExtras";
import { Leaderboard } from "@/components/Leaderboard";

const STATUS_STYLE = {
  pending: "text-yellow-400 border-yellow-400/30 bg-yellow-400/5",
  active: "text-emerald-400 border-emerald-400/30 bg-emerald-400/5",
  completed: "text-[#D4AF37] border-[#D4AF37]/30 bg-[#D4AF37]/5",
  rejected: "text-red-400 border-red-400/30 bg-red-400/5",
};

const inputCls =
  "w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 gold-focus transition-shadow";

export default function Dashboard() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [planId, setPlanId] = useState("");
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [proof, setProof] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
    api.get("/investments/my").then((r) => setInvestments(r.data)).catch(() => {});
  };

  useEffect(load, []);

  const uploadProof = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/investments/upload-proof", fd);
      setProof(data.path);
      toast.success("Payment screenshot attached");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const invest = async (e) => {
    e.preventDefault();
    if (!proof) {
      toast.error("Please upload your payment screenshot for verification");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/investments", { plan_id: planId, amount: Number(amount), payment_reference: reference, payment_proof: proof });
      toast.success("Investment request submitted. We'll verify your payment and confirm by email.");
      setPlanId("");
      setAmount("");
      setReference("");
      setProof("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const total = investments.filter((i) => i.status !== "rejected").reduce((s, i) => s + i.amount, 0);
  const activeCount = investments.filter((i) => i.status === "active").length;
  const selectedPlan = plans.find((p) => p.id === planId);

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="investor-dashboard">
      <div className="grain-overlay" />
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 lg:px-12 pt-32 pb-24">
        <p className="font-accent text-[11px] tracking-[0.3em] uppercase text-[#D4AF37] mb-4">Investor Portal</p>
        <h1 className="font-display text-4xl lg:text-5xl tracking-tighter" data-testid="dashboard-greeting">
          {user?.name ? `Welcome, ${user.name.split(" ")[0]}.` : "Your Portfolio"}
        </h1>

        <div className="mt-16 grid sm:grid-cols-3 gap-px bg-white/10 border border-white/10" data-testid="portfolio-stats">
          {[
            { label: "Total Committed", value: inr(total) },
            { label: "Active Investments", value: activeCount },
            { label: "Plans Available", value: plans.length },
          ].map((s) => (
            <div key={s.label} className="bg-[#0A0A0A] p-8">
              <p className="font-accent text-[10px] tracking-[0.25em] uppercase text-white/35">{s.label}</p>
              <p className="font-display text-3xl mt-3 text-white">{s.value}</p>
            </div>
          ))}
        </div>

        <ProfileSection />
        <KycSection />
        <SupportChat />

        <MaturityTracker investments={investments} />
        <div className="mt-16 grid lg:grid-cols-5 gap-16">
          <div className="lg:col-span-2">
            <h2 className="font-display text-2xl tracking-tight mb-8">Make an Investment</h2>
            <form onSubmit={invest} className="space-y-5" data-testid="invest-form">
              <select
                data-testid="invest-plan-select"
                required
                value={planId}
                onChange={(e) => setPlanId(e.target.value)}
                className={`${inputCls} bg-[#050505]`}
              >
                <option value="">Select a plan</option>
                {plans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title} — min {inr(p.min_investment)}
                  </option>
                ))}
              </select>
              <input
                data-testid="invest-amount-input"
                type="number"
                required
                min="1"
                placeholder={selectedPlan ? `Amount (min ${inr(selectedPlan.min_investment)})` : "Amount (₹)"}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className={inputCls}
              />
              {selectedPlan && (
                <div className="flex gap-4 items-center border border-white/10 p-4" data-testid="invest-plan-preview">
                  <img src={imgUrl(selectedPlan.image)} alt={selectedPlan.title} className="w-16 h-20 object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white">{selectedPlan.title}</p>
                    <p className="font-accent text-[10px] tracking-[0.2em] uppercase text-[#D4AF37] mt-1">
                      {selectedPlan.expected_return} · {durationLabel(selectedPlan)}
                    </p>
                    {selectedPlan.rate_table?.length > 0 && (
                      <RateChartDialog plan={selectedPlan}>
                        <button type="button" data-testid="dashboard-rate-chart-button" className="mt-2 font-accent text-[10px] tracking-[0.2em] uppercase text-white/50 hover:text-[#D4AF37] underline underline-offset-4 transition-colors">
                          View rate chart
                        </button>
                      </RateChartDialog>
                    )}
                  </div>
                </div>
              )}
              <div className="border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-4 space-y-3" data-testid="payment-step">
                <p className="font-accent text-[10px] tracking-[0.25em] uppercase text-[#D4AF37]">Step 2 · Pay & upload proof</p>
                <PaymentDetails amount={amount} />
                <p className="text-xs text-white/50 leading-relaxed">
                  After paying, attach the payment screenshot — our team verifies and approves it. Need help? Use the support chat below on this dashboard.
                </p>
                <input
                  data-testid="invest-reference-input"
                  placeholder="UTR / Transaction reference (optional)"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  className={inputCls}
                />
                <div className="flex items-center gap-4">
                  <label className="border border-white/20 px-5 py-3 font-accent text-[10px] tracking-[0.2em] uppercase text-white/70 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors cursor-pointer" data-testid="proof-upload-label">
                    {uploading ? "Uploading…" : proof ? "Replace Screenshot" : "Upload Payment Screenshot"}
                    <input data-testid="proof-upload-input" type="file" accept="image/*" className="hidden" onChange={uploadProof} disabled={uploading} />
                  </label>
                  {proof && <img src={imgUrl(proof)} alt="Payment proof" className="w-12 h-16 object-cover border border-emerald-400/40" data-testid="proof-preview" />}
                </div>
              </div>
              <button
                data-testid="invest-submit-button"
                disabled={submitting || uploading}
                className="w-full border border-[#D4AF37] text-[#D4AF37] py-4 font-accent text-[11px] tracking-[0.25em] uppercase hover:bg-[#D4AF37] hover:text-black transition-colors disabled:opacity-50"
              >
                {submitting ? "Submitting…" : "Submit for Approval"}
              </button>
            </form>
            <div className="mt-10">
              <ReferralCard />
              <Leaderboard />
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <h2 className="font-display text-2xl tracking-tight">My Investments</h2>
              {investments.length > 0 && <StatementButton />}
            </div>
            {investments.length === 0 ? (
              <p className="text-white/40 text-sm border border-white/10 p-8" data-testid="investments-empty">
                No investments yet. Choose a plan to begin.
              </p>
            ) : (
              <div className="border border-white/10" data-testid="investments-table">
                <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-white/10 font-accent text-[10px] tracking-[0.2em] uppercase text-white/35">
                  <span className="col-span-4">Plan</span>
                  <span className="col-span-2">Amount</span>
                  <span className="col-span-3">Date</span>
                  <span className="col-span-1">Proof</span>
                  <span className="col-span-2">Status</span>
                </div>
                {investments.map((inv) => (
                  <div key={inv.id} className="grid grid-cols-12 gap-4 px-6 py-5 border-b border-white/10 last:border-0 items-center" data-testid={`investment-row-${inv.id}`}>
                    <div className="col-span-4">
                      <p className="text-sm text-white">{inv.plan_title}</p>
                      <p className="font-accent text-[9px] tracking-[0.2em] uppercase text-white/35 mt-1">{inv.category}{inv.payment_reference ? ` · Ref ${inv.payment_reference}` : ""}</p>
                      {inv.admin_note && <p className="text-xs text-white/50 mt-2 italic" data-testid={`investment-note-${inv.id}`}>Note: {inv.admin_note}</p>}
                      {inv.payout && (
                        <p className="text-xs text-[#D4AF37] mt-2" data-testid={`investment-payout-${inv.id}`}>
                          Paid {inr(inv.payout.amount)} on {new Date(inv.payout.date).toLocaleDateString("en-IN")}{inv.payout.reference ? ` · Ref ${inv.payout.reference}` : ""}
                        </p>
                      )}
                    </div>
                    <span className="col-span-2 text-sm text-white">{inr(inv.amount)}</span>
                    <span className="col-span-3 text-sm text-white/50">{new Date(inv.created_at).toLocaleDateString("en-IN")}</span>
                    <span className="col-span-1">
                      {inv.payment_proof ? <img src={imgUrl(inv.payment_proof)} alt="Proof" className="w-8 h-10 object-cover border border-white/10" data-testid={`investment-proof-${inv.id}`} /> : <span className="text-[10px] text-white/30">—</span>}
                    </span>
                    <span className={`col-span-2 font-accent text-[10px] tracking-[0.15em] uppercase border px-2 py-1 text-center ${STATUS_STYLE[inv.status] || ""}`} data-testid={`investment-status-${inv.id}`}>
                      {inv.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
